import { useCallback, useEffect, useState } from 'react';

import { useOptionalFavorites } from '../contexts/FavoritesContext';
import { getOverview } from '../services/overview';
import { normalizeId } from '../utils/ids';
import { getSessionUserId, useOnSessionChange } from '../utils/session';

const NONE = Object.freeze({ dashboard: null, source: null, mine: null, collectionChoice: null });

/**
 * The dashboard a collection's Overview shows the caller (charts A7; plan
 * §4.6; ruled q04): GET /api/collections/{id}/overview — the cascade the
 * server resolves (the person's choice, else the collection's — never a
 * collection above's, KPR-1084), every tier re-checked there.
 *
 *   overview  { dashboard, source, mine, collectionChoice } — NONE
 *             until it answers (and on a failure: the section goes without)
 *   status    'loading' · 'ready' · 'error'
 *   reload    asks again
 *
 * It reads again when an Overview choice changes anywhere on this tab
 * (FavoritesContext `overviewVersion`) and when the collection's own choice
 * changes (`collectionChoice`, the document's `overviewDashboard_id`). Per
 * account: another person signing in drops what is held and any answer in
 * flight. `enabled: false` asks nothing (a reader the API answers 404).
 */
export default function useOverview(collectionId, { enabled = true, collectionChoice = null } = {}) {
	const favorites = useOptionalFavorites();
	const version = favorites?.overviewVersion || 0;
	const [overview, setOverview] = useState(NONE);
	const [status, setStatus] = useState('loading');
	const [token, setToken] = useState(0);
	const session = useOnSessionChange(() => {
		setOverview(NONE);
		setStatus('loading');
	});
	const choiceKey = normalizeId(collectionChoice) || '';

	useEffect(() => {
		if (!enabled || !collectionId) {
			setOverview(NONE);
			setStatus('ready');
			return undefined;
		}
		let active = true;
		const startedFor = getSessionUserId();
		setStatus((current) => (current === 'ready' ? current : 'loading'));
		getOverview(collectionId)
			.then((data) => {
				if (!active || getSessionUserId() !== startedFor) return;
				setOverview(data);
				setStatus('ready');
			})
			.catch(() => {
				if (!active || getSessionUserId() !== startedFor) return;
				setOverview(NONE);
				setStatus('error');
			});
		return () => { active = false; };
	}, [collectionId, enabled, version, choiceKey, token, session]);

	// Another collection never shows the last one's dashboard meanwhile.
	useEffect(() => {
		setOverview(NONE);
		setStatus('loading');
	}, [collectionId]);

	const reload = useCallback(() => setToken((n) => n + 1), []);
	return { overview, status, reload };
}
